import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sprout, Truck, Users, Building2 } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  { id: "farmer", label: "Hobby Farmer", icon: Sprout, desc: "Sell your harvest, meats, and bulk produce. Verification required." },
  { id: "driver", label: "Driver", icon: Truck, desc: "Pick up from hobby farmers, deliver to clients. Get paid per mile + tips." },
  { id: "client", label: "Client", icon: Users, desc: "Buy local, fresh produce direct from hobby farmers in your state." },
  { id: "wholesale", label: "Wholesale Buyer", icon: Building2, desc: "Bulk orders for stores, families, or community groups." },
];

export default function RoleSelect() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [states, setStates] = useState([]);
  const [chosen, setChosen] = useState(null);
  const [state, setState] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) navigate("/login", { replace: true });
    else if (user.role && user.role !== "pending") navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    api.get("/states").then((r) => setStates(r.data));
  }, []);

  const submit = async () => {
    if (!chosen || !state) {
      toast.error("Please select a role and your state.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/role", { role: chosen, state });
      await refresh();
      toast.success("Welcome to Azure Gypsy Farms — certificate of completion issued.");
      navigate("/dashboard", { replace: true });
    } catch (e) {
      toast.error("Could not save role. Try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <div className="max-w-5xl mx-auto px-6 sm:px-12 py-16">
      <div className="text-[11px] uppercase tracking-[0.3em] text-emerald-800 font-semibold">Onboarding</div>
      <h1 className="mt-4 font-serif text-4xl sm:text-5xl text-stone-900">Choose how you'd like to join.</h1>
      <p className="mt-3 font-sans text-stone-600 max-w-2xl">
        Each role unlocks a different dashboard. You can always switch later or contact us if you grow into multiple roles.
      </p>

      <div className="mt-10 grid sm:grid-cols-2 gap-5">
        {ROLES.map((r) => {
          const active = chosen === r.id;
          return (
            <button
              key={r.id}
              data-testid={`role-${r.id}`}
              onClick={() => setChosen(r.id)}
              className={`text-left p-6 border rounded-md bg-white transition-all ${
                active ? "border-emerald-800 ring-2 ring-emerald-800/40" : "border-stone-200 hover:border-stone-400"
              }`}
            >
              <div className={`w-11 h-11 rounded-full flex items-center justify-center ${active ? "bg-emerald-800 text-stone-50" : "bg-stone-100 text-stone-700"}`}>
                <r.icon size={20} />
              </div>
              <div className="mt-5 font-serif text-2xl text-stone-900">{r.label}</div>
              <p className="mt-2 font-sans text-sm text-stone-600">{r.desc}</p>
            </button>
          );
        })}
      </div>

      <div className="mt-10 max-w-md">
        <label className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">Your State</label>
        <div className="mt-2">
          <Select value={state} onValueChange={setState}>
            <SelectTrigger data-testid="state-select" className="bg-white border-stone-200">
              <SelectValue placeholder="Select your state" />
            </SelectTrigger>
            <SelectContent>
              {states.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Button
        data-testid="role-submit-btn"
        onClick={submit}
        disabled={saving}
        className="mt-8 bg-emerald-800 hover:bg-emerald-700 text-stone-50 px-8 py-6 rounded-md"
      >
        {saving ? "Saving..." : "Receive Certificate & Continue"}
      </Button>
    </div>
  );
}
